import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { Chip } from 'src/design/atoms/Chip';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { UserRateDto } from 'src/api/FiretrolApiClient';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { useUserRates } from 'src/api/queries/organization/useUserRates';
import { useAuth } from 'src/hooks/useAuth';
import { Header } from 'src/design/organisms/Header';
import { showSuccessToast } from 'src/utils';
import { EditUserRateForm } from './components/EditUserRateForm';
import { ImportUserRatesModal } from './components/ImportUserRatesModal';
import { PayCalculatorModal } from './components/PayCalculatorModal';
import { UserRateTable } from './components/UserRateTable';

export const Rates: React.FC = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: userRates, isLoading } = useUserRates();
  const [editUserRate, setEditUserRate] = React.useState<UserRateDto | null>(
    null,
  );
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const [calculatorOpen, setCalculatorOpen] = React.useState(false);

  const handleEdit = (userRate: UserRateDto) => {
    setEditUserRate(userRate);
    setEditModalOpen(true);
  };

  const handleSave = () => {
    setEditModalOpen(false);
    setEditUserRate(null);
  };

  const renderOffices = () => {
    if (
      !user ||
      !user.userOrganizationOffice ||
      user.userOrganizationOffice.length === 0
    ) {
      return <Chip>None</Chip>;
    }
    return user.userOrganizationOffice.map((uo) => {
      return (
        <Chip key={uo.organizationOffice?.id}>
          {uo.organizationOffice?.name}
        </Chip>
      );
    });
  };

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings', href: '/settings' }, { text: 'Rates' }],
        }}
        title="Rates"
        helpText="Manage user rates and benefits for project costing and payroll."
      />

      <Stack gap="xs" mb="md">
        <Group>
          <div>Your Access:</div>
          {renderOffices()}
        </Group>
      </Stack>

      <UserRateTable
        data={userRates ?? []}
        loading={isLoading}
        onEdit={handleEdit}
        onImport={() => setImportModalOpen(true)}
        onOpenCalculator={() => setCalculatorOpen(true)}
      />

      <BaseModal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditUserRate(null);
        }}
        title={`Edit User Rate for ${editUserRate?.fullName || 'User'}`}
        size="lg"
      >
        {editUserRate && (
          <EditUserRateForm
            userRate={editUserRate}
            onSave={handleSave}
            onCancel={() => {
              setEditModalOpen(false);
              setEditUserRate(null);
            }}
          />
        )}
      </BaseModal>

      <PayCalculatorModal
        opened={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
      />

      <ImportUserRatesModal
        opened={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={() => {
          invalidateFor('userRate.update', qc);
          showSuccessToast('User rates imported successfully');
        }}
      />
    </>
  );
};
